import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, MessageSquare } from "lucide-react";
import { PROJECTS } from "@/constants/testIds";

const scrollToContact = () => {
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

function DeviceMockup({ project }) {
  return (
    <div className="device-stack relative w-full">
      {/* Desktop frame */}
      <div className="relative w-full rounded-xl border border-white/10 bg-[#0C0C0C] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.8)] overflow-hidden">
        {/* Browser chrome */}
        <div className="h-8 sm:h-10 bg-[#141414] border-b border-white/5 flex items-center px-3 sm:px-4 gap-1.5 sm:gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          <div className="ml-3 sm:ml-5 flex-1 max-w-md bg-black/60 border border-white/5 rounded-md h-4 sm:h-5 flex items-center px-2 gap-1.5">
            <span className="w-1 h-1 rounded-full bg-[#E2FF31]" />
            <span className="text-[9px] sm:text-[10px] font-mono-tag text-white/50 truncate tracking-[0.15em]">
              {project.browserUrl}
            </span>
          </div>
        </div>
        {/* Screen */}
        <div className="aspect-[16/10] w-full overflow-hidden bg-[#050505]">
          <img
            src={project.desktopImg}
            alt={`${project.title} desktop preview`}
            loading="lazy"
            className="device-desktop-img w-full h-full object-cover object-top"
          />
          {/* Reflection sweep */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
            style={{
              background:
                "linear-gradient(115deg, transparent 40%, rgba(226,255,49,0.10) 50%, transparent 60%)",
            }}
          />
        </div>
      </div>

      {/* Mobile frame */}
      <div className="device-mobile-wrapper absolute -bottom-10 sm:-bottom-16 -right-3 sm:-right-10 w-[34%] sm:w-[30%] min-w-[120px] max-w-[240px] z-20">
        <div className="relative rounded-[1.6rem] sm:rounded-[2rem] border-[4px] sm:border-[8px] border-[#141414] bg-[#050505] shadow-[0_40px_80px_-15px_rgba(0,0,0,0.9)] overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[45%] h-[10px] sm:h-[16px] bg-[#141414] rounded-b-xl sm:rounded-b-2xl z-30" />
          <div className="aspect-[9/19] w-full overflow-hidden bg-[#050505]">
            <img
              src={project.mobileImg}
              alt={`${project.title} mobile preview`}
              loading="lazy"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ project }) {
  const isLive = project.status === "LIVE";
  return (
    <span
      data-testid={PROJECTS.badgeStatus(project.slug)}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-mono-tag text-[10px] sm:text-xs ${
        isLive
          ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]"
          : "border-[#3B82F6]/40 bg-[#3B82F6]/10 text-[#3B82F6]"
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          isLive ? "bg-[#10B981] pulse-dot" : "bg-[#3B82F6]"
        }`}
      />
      {project.statusLabel}
    </span>
  );
}

export default function ProjectCard({ project }) {
  const isRight = project.align === "right";
  const cardRef = useRef(null);

  // Parallax tilt on desktop only
  const mvX = useMotionValue(0);
  const mvY = useMotionValue(0);
  const rotX = useTransform(useSpring(mvY, { stiffness: 100, damping: 20 }), [-0.5, 0.5], [4, -4]);
  const rotY = useTransform(useSpring(mvX, { stiffness: 100, damping: 20 }), [-0.5, 0.5], [-6, 6]);

  const handleMove = (e) => {
    if (window.matchMedia("(hover: none)").matches) return;
    const rect = e.currentTarget.getBoundingClientRect();
    mvX.set((e.clientX - rect.left) / rect.width - 0.5);
    mvY.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleLeave = () => {
    mvX.set(0);
    mvY.set(0);
  };

  return (
    <motion.article
      ref={cardRef}
      data-testid={PROJECTS.card(project.slug)}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="project-card relative group"
    >
      {/* Massive outlined number */}
      <div
        aria-hidden
        className={`font-display outline-number text-[9rem] sm:text-[13rem] lg:text-[18rem] leading-none absolute -top-10 lg:-top-20 pointer-events-none select-none ${
          isRight ? "right-0" : "-left-2 lg:-left-6"
        }`}
      >
        {project.number}
      </div>

      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center pt-16 lg:pt-24">
        {/* Content column */}
        <div
          className={`lg:col-span-4 order-2 ${
            isRight ? "lg:order-2" : "lg:order-1"
          }`}
        >
          <div className="flex items-center gap-3 mb-6">
            <StatusBadge project={project} />
            <span className="font-mono-tag text-[10px] text-white/40">
              {project.industry}
            </span>
          </div>

          <h3 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6">
            {project.title}
          </h3>

          <p className="text-white/65 text-base leading-relaxed mb-8 max-w-lg">
            {project.overview}
          </p>

          <div className="mb-8">
            <div className="font-mono-tag text-[10px] text-white/40 mb-3">
              // Features
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 max-w-md">
              {project.features.map((f) => (
                <div
                  key={f}
                  className="flex items-center gap-2 text-sm text-white/80"
                >
                  <span className="text-[#E2FF31]">+</span>
                  {f}
                </div>
              ))}
            </div>
          </div>

          <div className="mb-10">
            <div className="font-mono-tag text-[10px] text-white/40 mb-3">
              // Technology
            </div>
            <div className="flex flex-wrap gap-2">
              {project.tech.map((t) => (
                <span
                  key={t}
                  className="px-3 py-1.5 border border-white/15 rounded-full font-mono-tag text-[10px] text-white/80 hover:border-[#E2FF31] hover:text-[#E2FF31] transition-colors"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <a
              data-testid={PROJECTS.liveDemo(project.slug)}
              data-cursor="Open demo"
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              View Live Demo
              <ArrowUpRight size={16} className="arrow" />
            </a>
            <button
              data-testid={PROJECTS.requestSimilar(project.slug)}
              data-cursor="Get this built"
              onClick={scrollToContact}
              className="btn-ghost"
            >
              <MessageSquare size={14} />
              Request Similar Website
            </button>
          </div>
        </div>

        {/* Mockup column (larger + tilt) */}
        <motion.div
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
          style={{
            rotateX: rotX,
            rotateY: rotY,
            transformStyle: "preserve-3d",
            perspective: 1200,
          }}
          className={`lg:col-span-8 order-1 relative pr-4 sm:pr-10 pb-16 sm:pb-24 ${
            isRight ? "lg:order-1" : "lg:order-2"
          }`}
        >
          <DeviceMockup project={project} />
        </motion.div>
      </div>
    </motion.article>
  );
}
